import { useState, useEffect } from 'react';
import GroupPicker from './GroupPicker.jsx';
import { convertIsoToLocalDate } from '../../../../libs.jsx';

/**
 * ApproveAddDialog — complex move-in approval dialog with read-only
 * resident information and an embedded group picker.
 *
 * @param {{
 *   task: object | null,
 *   groups: Array,
 *   onApprove: (task: object, groupIds: string[]) => void,
 *   onReject: (task: object) => void,
 *   onClose: () => void,
 *   submitting: boolean,
 * }} props
 */
const ApproveAddDialog = ({ task, groups, onApprove, onReject, onClose, submitting }) => {
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);

  // Reset group selection when task changes (dialog opened for new task)
  useEffect(() => {
    setSelectedGroupIds([]);
  }, [task]);

  if (!task) return null;

  const moveInDate = task.physical_move_in_date
    ? convertIsoToLocalDate(task.physical_move_in_date)
    : '';

  return (
    <div className="dp-drawer-backdrop" onClick={onClose} aria-hidden="true">
      <div
        className="ehr-dialog ehr-dialog--wide"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Approve Resident Add"
      >
        {/* Header */}
        <div className="ehr-dialog__header">
          <h3 className="ehr-dialog__title">Approve Resident Add</h3>
          <button
            type="button"
            onClick={onClose}
            className="dp-drawer__close"
            aria-label="Close dialog"
          >
            <svg className="dp-icon-lg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Warning banner */}
        <div className="ehr-dialog__warning">
          ATTENTION! Must still attach pendant devices after approval!
        </div>

        {/* Read-only fields */}
        <div className="ehr-dialog__body">
          <div className="ehr-dialog__field-row">
            <div className="ehr-dialog__field">
              <span className="ehr-dialog__field-label">Area Name</span>
              <span className="ehr-dialog__field-value">{task.area_name || '\u2014'}</span>
            </div>
          </div>
          <div className="ehr-dialog__field-row">
            <div className="ehr-dialog__field">
              <span className="ehr-dialog__field-label">First Name</span>
              <span className="ehr-dialog__field-value">{task.resident_first_name || '\u2014'}</span>
            </div>
            <div className="ehr-dialog__field">
              <span className="ehr-dialog__field-label">Last Name</span>
              <span className="ehr-dialog__field-value">{task.resident_last_name || '\u2014'}</span>
            </div>
          </div>
          <div className="ehr-dialog__field-row">
            <div className="ehr-dialog__field">
              <span className="ehr-dialog__field-label">Nickname</span>
              <span className="ehr-dialog__field-value">{task.resident_nickname || '\u2014'}</span>
            </div>
            <div className="ehr-dialog__field">
              <span className="ehr-dialog__field-label">Care Level</span>
              <span className="ehr-dialog__field-value">{task.ehr_level_of_care_id || '\u2014'}</span>
            </div>
          </div>
          <div className="ehr-dialog__field-row">
            <div className="ehr-dialog__field">
              <span className="ehr-dialog__field-label">Room Phone #</span>
              <span className="ehr-dialog__field-value">{task.resident_phone || '\u2014'}</span>
            </div>
            <div className="ehr-dialog__field">
              <span className="ehr-dialog__field-label">Email Address</span>
              <span className="ehr-dialog__field-value">{task.resident_email || '\u2014'}</span>
            </div>
          </div>
          <div className="ehr-dialog__field-row">
            <div className="ehr-dialog__field">
              <span className="ehr-dialog__field-label">Move-in Date</span>
              <span className="ehr-dialog__field-value">{moveInDate || '\u2014'}</span>
            </div>
            <div className="ehr-dialog__field">
              <span className="ehr-dialog__field-label">EHR ID</span>
              <span className="ehr-dialog__field-value">{task.ehr_resident_id || '\u2014'}</span>
            </div>
          </div>

          {/* Divider */}
          <hr className="ehr-dialog__divider" />

          {/* Group picker */}
          <GroupPicker
            groups={groups}
            selectedIds={selectedGroupIds}
            onSelectionChange={setSelectedGroupIds}
          />
        </div>

        {/* Footer */}
        <div className="ehr-dialog__footer">
          <div className="ehr-dialog__footer-left">
            <button
              type="button"
              onClick={() => onReject(task)}
              className="dp-btn dp-btn--danger-outline"
              disabled={submitting}
            >
              Reject
            </button>
          </div>
          <div className="ehr-dialog__footer-right">
            <button
              type="button"
              onClick={onClose}
              className="dp-btn dp-btn--secondary"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onApprove(task, selectedGroupIds)}
              disabled={submitting}
              className="dp-btn dp-btn--primary"
            >
              {submitting ? 'Approving...' : 'Approve'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApproveAddDialog;
